<script lang="ts">
  // Interactive override for the /demo (and /jetzt-starten-demo) pages. Reuses the
  // layouted markup/classes from the generated partial but wires working
  // actions: "Demo-Token holen" → POST /demo/token (token + 3-day countdown),
  // and "Demo-Login" → the web-student demo autologin URL. German copy. Handles
  // the disabled/403 case gracefully ("Demo derzeit nicht verfügbar").
  import { onMount, onDestroy } from "svelte";
  import Header from "./Header.svelte";
  import Footer from "./Footer.svelte";
  import { pageById } from "./pages";
  import { applyBehaviors } from "./behaviors";
  import {
    altchaChallengeUrl,
    requestDemoToken,
    type DemoTokenResult,
  } from "./api";

  // Which generated page this override stands in for ("demo" or "jetzt-starten-demo")
  // — only used for the document title/description parity.
  export let id: "demo" | "jetzt-starten-demo" = "demo";

  // The web-student demo autologin URL. Baked at build via
  // VITE_STUDENT_DEMO_URL, with a sensible prod default that matches the
  // student app's hash route.
  const STUDENT_DEMO_URL =
    (import.meta.env.VITE_STUDENT_DEMO_URL as string | undefined) ??
    "https://student.wiederdabei.de/#/demo";

  const meta = pageById[id];

  let main: HTMLElement;
  let cleanup: (() => void) | null = null;

  let loading = false;
  let result: DemoTokenResult | null = null;
  let copied = false;

  // Token request requires the tester's email plus an Altcha solution + honeypot
  // (same captcha/honeypot mechanism as the contact form).
  let email = "";
  let hp = ""; // honeypot — kept empty by humans
  let altchaReady = false;
  let formErrorMsg: string | null = null;
  let formEl: HTMLFormElement;

  // Live countdown for the 3-day expiry.
  let now = Date.now();
  let ticker: ReturnType<typeof setInterval> | null = null;

  $: token = result?.ok ? result.token ?? "" : "";
  $: expiresAt = result?.ok ? result.expiresAt ?? null : null;
  $: remainingMs = expiresAt != null ? Math.max(0, expiresAt - now) : 0;

  // Friendly DE error copy for the disabled/limited/validation/network cases.
  $: errorMsg = (() => {
    if (!result || result.ok) return null;
    switch (result.error) {
      case "demo_disabled":
        return "Demo derzeit nicht verfügbar.";
      case "rate_limited":
        return "Zu viele Anfragen. Bitte später erneut versuchen.";
      case "demo_room_missing":
        return "Demo ist gerade nicht bereit. Bitte später erneut versuchen.";
      case "email_required":
        return "Bitte eine E-Mail-Adresse angeben.";
      case "email_invalid":
        return "Bitte eine gültige E-Mail-Adresse angeben.";
      case "altcha_invalid":
        return "Die Sicherheitsprüfung ist fehlgeschlagen. Bitte erneut versuchen.";
      case "network":
        return "Netzwerkfehler. Bitte Verbindung prüfen und erneut versuchen.";
      default:
        return "Demo-Token konnte nicht erstellt werden. Bitte später erneut versuchen.";
    }
  })();

  function formatCountdown(ms: number): string {
    const total = Math.floor(ms / 1000);
    const d = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d} Tag${d === 1 ? "" : "e"} ${pad(h)}:${pad(m)}:${pad(s)}`;
  }

  function formatDate(ms: number): string {
    const dt = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(dt.getDate())}.${pad(dt.getMonth() + 1)}.${dt.getFullYear()} ${pad(
      dt.getHours(),
    )}:${pad(dt.getMinutes())}`;
  }

  // Read the Altcha widget's hidden input value (the solution payload).
  function readAltcha(): string {
    const input = formEl?.querySelector<HTMLInputElement>(
      'input[name="altcha"]',
    );
    return input?.value ?? "";
  }

  async function getToken() {
    formErrorMsg = null;
    if (!email.trim()) {
      formErrorMsg = "Bitte eine E-Mail-Adresse angeben.";
      return;
    }
    const altcha = readAltcha();
    if (!altcha) {
      formErrorMsg =
        "Bitte die Sicherheitsprüfung abschließen (Häkchen setzen).";
      return;
    }
    loading = true;
    copied = false;
    result = await requestDemoToken({ email: email.trim(), altcha, hp });
    loading = false;
  }

  async function copyToken() {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      copied = true;
      setTimeout(() => (copied = false), 2000);
    } catch {
      /* clipboard unavailable — user can still select the text manually */
    }
  }

  onMount(() => {
    // Register the <altcha-widget> custom element + German translations.
    // Sequential: core must run first to create globalThis.$altcha, which
    // altcha/i18n/de checks before registering German strings. Parallel
    // (Promise.all) races and can skip the registration → English fallback.
    import("altcha")
      .then(() => import("altcha/i18n/de"))
      .then(() => (altchaReady = true));
    if (meta?.title) document.title = meta.title;
    let desc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.name = "description";
      document.head.appendChild(desc);
    }
    desc.content = meta?.description ?? "";
    window.scrollTo({ top: 0, behavior: "auto" });
    if (main) cleanup = applyBehaviors(main);
    ticker = setInterval(() => (now = Date.now()), 1000);
    return () => cleanup?.();
  });

  onDestroy(() => {
    if (ticker) clearInterval(ticker);
  });
</script>

<Header active={meta?.nav ?? "demo"} />
<main bind:this={main}>
  <div class="wrap" style="padding-block:clamp(28px,4vw,44px) 0">
    <div class="crumb">
      <a href="/">Start</a><span class="sep">›</span><span class="here">Demo</span>
    </div>
  </div>
  <section class="section" style="padding-top:clamp(22px,3vw,34px)">
    <div class="wrap" style="max-width:880px">
      <span class="eyebrow">Demo</span>
      <h1 class="title" style="font-size:clamp(30px,4.2vw,50px); margin:14px 0 18px">
        Probier es aus
      </h1>
      <div class="prose" style="max-width:none; margin-top:10px">
        <p>
          Zwei Wege, wiederdabei live zu erleben: dein eigenes Demo-Gerät oder
          ein direkter Demo-Login in die Schüler-App.
        </p>

        <h2>(a) Eigenes Demo-Gerät</h2>
        <p>
          Hol dir ein Demo-Token. Es ist <strong>3 Tage gültig</strong> und läuft
          danach automatisch ab. Während der Demo zeigt sich auf dem Schüler- und
          Schulgerät dauerhaft ein <strong>„Test-Modus“-Hinweis</strong>.
        </p>
        <p>
          Du willst dein eigenes Gerät dauerhaft aufsetzen?
          <a href="/jetzt-starten-geraet">Zur Klassenzimmer-Gerät-Schnellanleitung →</a>
        </p>

        <div class="demo-card" data-testid="demo-token-card">
          {#if result?.ok && token}
            <p class="demo-card__label">Dein Demo-Token</p>
            <code class="demo-token" data-testid="demo-token">{token}</code>
            <div class="demo-card__row">
              <button
                type="button"
                class="btn btn--ghost"
                on:click={copyToken}
                data-testid="demo-copy"
              >
                {copied ? "Kopiert ✓" : "Kopieren"}
              </button>
            </div>
            {#if expiresAt != null}
              <p class="demo-card__meta" data-testid="demo-countdown">
                Läuft ab in <strong>{formatCountdown(remainingMs)}</strong>
                <span class="demo-card__sub">(am {formatDate(expiresAt)})</span>
              </p>
            {/if}
            <p class="demo-card__note">
              Hinweis: Während der Demo erscheint dauerhaft ein
              „Test-Modus“-Hinweis auf dem Schüler- und Schulgerät.
            </p>
          {:else}
            <form
              class="demo-form"
              bind:this={formEl}
              on:submit|preventDefault={getToken}
              novalidate
            >
              {#if formErrorMsg}
                <p
                  class="demo-card__error"
                  role="alert"
                  data-testid="demo-form-error"
                >
                  {formErrorMsg}
                </p>
              {:else if errorMsg}
                <p
                  class="demo-card__error"
                  role="alert"
                  data-testid="demo-error"
                >
                  {errorMsg}
                </p>
              {/if}

              <label class="demo-field">
                <span>E-Mail</span>
                <input
                  type="email"
                  bind:value={email}
                  maxlength="320"
                  required
                  autocomplete="email"
                  placeholder="dein.name@beispiel.de"
                  data-testid="demo-email"
                />
              </label>

              <!-- Honeypot: visually hidden + off-screen; bots fill it, humans don't. -->
              <div class="demo-hp" aria-hidden="true">
                <label>
                  Bitte dieses Feld leer lassen
                  <input
                    type="text"
                    bind:value={hp}
                    tabindex="-1"
                    autocomplete="off"
                  />
                </label>
              </div>

              <div class="demo-altcha">
                {#if altchaReady}
                  <!-- svelte-ignore a11y-unknown-element -->
                  <altcha-widget
                    challenge={altchaChallengeUrl()}
                    language="de"
                  ></altcha-widget>
                {:else}
                  <p class="demo-card__hint">Sicherheitsprüfung wird geladen…</p>
                {/if}
              </div>

              <button
                type="submit"
                class="btn btn--primary"
                disabled={loading}
                data-testid="demo-get-token"
              >
                {loading
                  ? "Wird erstellt…"
                  : errorMsg
                    ? "Erneut versuchen"
                    : "Demo-Token holen"}
              </button>

              <p class="demo-card__hint">
                Wir senden dir das Demo-Token an diese Adresse. Zum Schutz vor
                Spam nutzt das Formular eine selbstgehostete
                Altcha-Sicherheitsprüfung.
              </p>
            </form>
          {/if}
        </div>

        <h2>(b) Demo-Login</h2>
        <p>
          Direkt einloggen: Wir öffnen die Schüler-App mit einem Demo-Konto
          (demo:demo). Du siehst ein Beispielbild, wo sonst das Live-Video läuft.
        </p>
        <p>
          <a
            class="btn btn--primary"
            href={STUDENT_DEMO_URL}
            data-testid="demo-login-link"
          >
            Demo-Login öffnen
          </a>
        </p>
      </div>
    </div>
  </section>
</main>
<Footer />

<style>
  .demo-card {
    margin: 18px 0 8px;
    padding: 18px 20px;
    border: 1px solid var(--line, #d4d8de);
    border-radius: 14px;
    background: var(--surface, #fff);
    display: grid;
    gap: 12px;
    max-width: 620px;
  }
  .demo-card__label {
    margin: 0;
    font-weight: 600;
    color: var(--muted, #5a6270);
  }
  .demo-token {
    display: block;
    padding: 12px 14px;
    border-radius: 10px;
    background: var(--code-bg, #0f172a);
    color: #e2e8f0;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 14px;
    word-break: break-all;
    user-select: all;
  }
  .demo-card__row {
    display: flex;
    gap: 10px;
  }
  .demo-card__meta {
    margin: 0;
    font-size: 15px;
  }
  .demo-card__sub {
    color: var(--muted, #5a6270);
    font-size: 13px;
  }
  .demo-card__note {
    margin: 0;
    font-size: 13px;
    color: var(--muted, #5a6270);
  }
  .demo-card__error {
    margin: 0;
    color: #b00020;
    background: rgba(176, 0, 32, 0.08);
    border: 1px solid rgba(176, 0, 32, 0.25);
    padding: 10px 12px;
    border-radius: 10px;
  }
  .demo-card__hint {
    margin: 0;
    color: var(--muted, #5a6270);
    font-size: 13px;
  }
  .demo-form {
    display: grid;
    gap: 14px;
  }
  .demo-field {
    display: grid;
    gap: 6px;
  }
  .demo-field > span {
    font-weight: 600;
    color: var(--ink, inherit);
  }
  .demo-field input {
    width: 100%;
    padding: 11px 13px;
    border: 1px solid var(--line, #d4d8de);
    border-radius: 10px;
    background: var(--surface, #fff);
    color: var(--ink, #1a1d22);
    font: inherit;
  }
  .demo-field input:focus {
    outline: none;
    border-color: var(--primary, #2f6df6);
    box-shadow: 0 0 0 3px rgba(47, 109, 246, 0.18);
  }
  .demo-altcha {
    margin-block: 2px;
  }
  .demo-form .btn {
    justify-self: start;
  }
  /* Honeypot: removed from layout + invisible, still focusable by bots only. */
  .demo-hp {
    position: absolute !important;
    left: -9999px !important;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }
</style>
